import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";
import { knowledgeBaseService, type KnowledgeBaseItem } from "@/services/knowledgeBaseService";

/** Canonical stale time for full document rows (aligned with summary list). */
export const KNOWLEDGE_BASE_ITEM_STALE_MS = 1000 * 60 * 5;

export function knowledgeBaseItemQueryKey(id: string): QueryKey {
  return ["knowledgebase-item", id];
}

export function knowledgeBaseItemQueryOptions(id: string) {
  return {
    queryKey: knowledgeBaseItemQueryKey(id),
    queryFn: async (): Promise<KnowledgeBaseItem | null> => {
      if (!id || id === "__none__") return null;
      return knowledgeBaseService.getKnowledgeBaseItem(id);
    },
    staleTime: KNOWLEDGE_BASE_ITEM_STALE_MS,
  };
}

export function invalidateKnowledgeBaseItem(queryClient: QueryClient, id: string) {
  return queryClient.invalidateQueries({ queryKey: knowledgeBaseItemQueryKey(id) });
}

export async function ensureKnowledgeBaseItem(
  queryClient: QueryClient,
  id: string
): Promise<KnowledgeBaseItem | null> {
  if (!id || id === "__none__") return null;
  try {
    return await queryClient.ensureQueryData({
      ...knowledgeBaseItemQueryOptions(id),
    });
  } catch {
    return null;
  }
}

export function prefetchKnowledgeBaseItem(queryClient: QueryClient, id: string) {
  if (!id || id === "__none__") return Promise.resolve();
  return queryClient.prefetchQuery({
    ...knowledgeBaseItemQueryOptions(id),
  });
}

function applyKnowledgeBasePatch(
  item: KnowledgeBaseItem,
  patch: Partial<KnowledgeBaseItem>
): KnowledgeBaseItem {
  const next = { ...item, ...patch };
  if (patch.tagIds !== undefined) {
    let metadata = next.metadata ?? {};
    if (typeof metadata === "string") {
      try {
        metadata = JSON.parse(metadata);
      } catch {
        metadata = {};
      }
    }
    next.metadata = { ...(metadata as Record<string, unknown>), tagIds: patch.tagIds };
  }
  return next;
}

/** Update cached full item + summary list rows without refetching the whole library. */
export function patchKnowledgeBaseItemInCaches(
  queryClient: QueryClient,
  id: string,
  patch: Partial<KnowledgeBaseItem>
) {
  queryClient.setQueryData<KnowledgeBaseItem | null>(
    knowledgeBaseItemQueryKey(id),
    (old) => (old && old.id === id ? applyKnowledgeBasePatch(old, patch) : old)
  );

  queryClient.setQueriesData<InfiniteData<KnowledgeBaseItem[]>>(
    { queryKey: ["knowledgebase-summary"] },
    (old) => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map((page) =>
          page.map((item) =>
            item.id === id ? applyKnowledgeBasePatch(item, patch) : item
          )
        ),
      };
    }
  );
}
